import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X, Plus } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const DISMISS_KEY = "kewag_pwa_install_dismissed";
const HIDE_ON = ["/", "/login", "/admin/login", "/cockpit/login", "/reset-password", "/preise"];

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallAppButton() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); setDeferred(null); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari feuert kein beforeinstallprompt → Hinweis anzeigen
    if (isIOS()) { setIosHint(true); setShow(true); }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setShow(false); setShowIosSheet(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (iosHint) { setShowIosSheet(true); return; }
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null); setShow(false);
  };

  if (!show || HIDE_ON.includes(pathname)) return null;

  return (
    <>
      <div className="fixed z-[70] left-1/2 -translate-x-1/2 bottom-24 lg:left-6 lg:translate-x-0 lg:bottom-6 flex items-center gap-2 rounded-full border border-[#A3E635]/40 bg-[#0f1206]/95 backdrop-blur px-2 py-2 shadow-2xl shadow-black/50"
        data-testid="pwa-install-bar">
        <button onClick={install} data-testid="pwa-install-btn"
          className="flex items-center gap-2 rounded-full bg-[#A3E635] text-[#071005] font-bold text-sm pl-3 pr-4 py-2 active:scale-95 transition-transform">
          {iosHint ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
          {t("App installieren")}
        </button>
        <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label={t("Schliessen")}
          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIosSheet && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4" data-testid="pwa-ios-hint">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowIosSheet(false)} />
          <div className="relative w-full max-w-sm bg-[#16161c] border border-white/10 rounded-2xl p-6 text-center">
            <img src="/icon-192.png" alt="KEWAGpage" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
            <h3 className="font-bold text-lg text-white">{t("Zum Startbildschirm hinzufügen")}</h3>
            <p className="text-sm text-gray-400 mt-2">{t("So installieren Sie das KEWAGpage-Cockpit als App auf Ihrem iPhone oder iPad:")}</p>
            <ol className="text-left text-sm text-gray-300 mt-4 space-y-2.5">
              <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">1</span> {t("Tippen Sie unten auf das Teilen-Symbol")} <Share className="w-4 h-4 text-[#A3E635]" /></li>
              <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">2</span> {t("Wählen Sie «Zum Home-Bildschirm»")} <Plus className="w-4 h-4 text-[#A3E635]" /></li>
              <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-[#A3E635] text-[#071005] font-bold flex items-center justify-center text-xs shrink-0">3</span> {t("Bestätigen Sie mit «Hinzufügen»")}</li>
            </ol>
            <button onClick={() => setShowIosSheet(false)} data-testid="pwa-ios-close"
              className="mt-6 w-full font-bold py-2.5 rounded-xl bg-[#A3E635] text-[#071005] active:scale-95 transition-transform">{t("Verstanden")}</button>
          </div>
        </div>
      )}
    </>
  );
}
